import React, { useState, useEffect } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";

const BASE_URL = process.env.REACT_APP_VOICE_TO_VOICE_URL;

const ImageAssessment = () => {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState("cars");
  const [categories] = useState(["cars", "bikes", "animals", "flowers"]);

  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);

  // CHANGED: Initialize as null. We will store an object { key: '...', suffix: '...' }
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [evaluationStatus, setEvaluationStatus] = useState("");
  const [error, setError] = useState("");

  const [assessmentResult, setAssessmentResult] = useState(null);
  const [allFeedbacks, setAllFeedbacks] = useState([]);

  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } =
    useReactMediaRecorder({
      audio: true,
      video: false,
      blobPropertyBag: { type: "audio/wav" },
      audioConstraints: { sampleRate: 48000 },
    });

  // Fetch images when category changes
  useEffect(() => {
    if (!category) return;
    fetchImages(category);
  }, [category]);

  const fetchImages = async (selectedCategory) => {
    setLoadingImages(true);
    setError("");

    try {
      const res = await axios.get(
        `${BASE_URL}/softskills/quicktalk/images?category=${selectedCategory}`
      );

      if (res.data.status === "success") {
        setImages(res.data.images);
        setCurrentIndex(0);
        setAllFeedbacks([]);
      } else {
        setError(t("imageAssessment.errors.noImagesFound"));
      }
    } catch (err) {
      setError(t("imageAssessment.errors.loadImages"));
    } finally {
      setLoadingImages(false);
    }
  };

  const currentImage = images[currentIndex];

  const imageDescription =
    i18n.language === "te"
      ? currentImage?.description_telugu
      : currentImage?.description;

  const imageFeedback =
    i18n.language === "te"
      ? assessmentResult?.telugu_feedback
      : assessmentResult?.feedback;

  // SUBMIT RECORDING
  const handleSubmitRecording = async () => {
    if (!mediaBlobUrl) return alert("Record audio first!");

    // CHANGED: Store the KEY, not the text
    setSubmissionStatus({ key: "imageAssessment.status.uploading" });
    setError("");
    setAssessmentResult(null);

    try {
      const audioBlob = await fetch(mediaBlobUrl).then((res) => res.blob());
      const formData = new FormData();
      formData.append("audio_file", audioBlob, "recording.wav");

      const res = await axios.post(
        `${BASE_URL}/softskills/quicktalk/submit`,
        formData
      );

      const s3_key = res.data.s3_key;

      // CHANGED: Store the KEY
      setSubmissionStatus({ key: "imageAssessment.status.queued" });

      await enqueueProcessing(s3_key);
    } catch (err) {
      setError(t("imageAssessment.errors.uploadFailed"));
      setSubmissionStatus(null);
    }
  };

  // ENQUEUE PROCESSING
  const enqueueProcessing = async (s3_key) => {
    try {
      const payload = {
        image_id: currentImage.id,
        metadata: {
          category,
          description: currentImage.description,
          photographer: currentImage.photographer,
        },
        s3_key,
        quicktalk_session_id: null,
      };

      const res = await axios.post(
        `${BASE_URL}/softskills/quicktalk/enqueue`,
        payload
      );

      const sessionId = res.data.quicktalk_session_id;

      // CHANGED: Store the KEY
      setSubmissionStatus({ key: "imageAssessment.status.jobQueued" });

      const MAX_RETRIES = 10;
      const DELAY_MS = 3000;

      for (let i = 0; i < MAX_RETRIES; i++) {
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
        
        // CHANGED: Store KEY and dynamic suffix separately
        setSubmissionStatus({ 
          key: "imageAssessment.status.evaluating", 
          suffix: ` (${i + 1}/${MAX_RETRIES})` 
        });

        try {
          await evaluateSession(sessionId);
          // CHANGED: Store KEY
          setSubmissionStatus({ key: "imageAssessment.status.complete" });
          return;
        } catch (err) {
          const errorDetail = err.response?.data?.detail;

          if (
            errorDetail === "No transcripts found for session" &&
            i < MAX_RETRIES - 1
          ) {
            continue;
          }

          throw err;
        }
      }

      throw new Error(t("imageAssessment.errors.timeout"));
    } catch (err) {
      setError(`${t("imageAssessment.errors.evaluateFailed")}: ${err.message}`);
      setSubmissionStatus(null);
    }
  };

  // FINAL EVALUATION
  const evaluateSession = async (sessionId) => {
    const res = await axios.post(`${BASE_URL}/softskills/quicktalk/evaluate`, {
      quicktalk_session_id: sessionId,
    });

    if (res.data.status === "success") {
      const feedback = res.data.feedback;
      const telugu_feedback = res.data.telugu_feedback;

      setAllFeedbacks((prev) => [
        ...(prev || []),
        {
          imageId: currentImage.id,
          imageDesc: currentImage.description,
          feedback,
          telugu_feedback,
        },
      ]);

      setAssessmentResult({
        feedback,
        telugu_feedback,
      });

      setEvaluationStatus(t("imageAssessment.status.complete"));
    } else {
      throw new Error("Evaluation failed");
    }
  };

  const handleNext = () => {
    clearBlobUrl();
    setAssessmentResult(null);
    setSubmissionStatus(null);
    setEvaluationStatus("");
    setError("");

    setCurrentIndex((prev) => prev + 1);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setAllFeedbacks([]);
    fetchImages(category);
  };

  const isCompleted = !loadingImages && images.length > 0 && currentIndex >= images.length;

  return (
    <div className="container mx-auto p-4 max-w-2xl font-sans bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800 pt-4">
        {t("imageAssessment.title")}
      </h1>

      {/* CATEGORY DROPDOWN */}
      <div className="mb-6 flex items-center justify-center">
        <label className="font-medium mr-3 text-gray-700">
          {t("imageAssessment.selectCategory")}:
        </label>

        <select
          className="border border-gray-300 p-2 rounded-lg shadow-sm bg-white"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {t(`imageAssessment.categories.${c}`)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 p-3 rounded-lg mb-4">
          🚨 <strong>Error:</strong> {error}
        </div>
      )}

      {isCompleted ? (
        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center animate-fade-in">
          <div className="mb-6">
            <span className="text-6xl">🎉</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t("imageAssessment.completed")}
          </h2>
          <p className="text-gray-600 mb-6">
            {t("imageAssessment.completedMessage")}
          </p>

          <button
            onClick={handleRestart}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition-colors duration-200"
          >
           {t("imageAssessment.restart")}
          </button>

        </div>
      ) : (
        <>
          {/* IMAGE SECTION */}
          <div className="mb-6 p-6 bg-white border border-gray-200 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-center text-gray-800">
              {loadingImages
                ? t("imageAssessment.loadingImages")
                : imageDescription || t("imageAssessment.noImage")}
            </h2>

            {!loadingImages && currentImage && (
              <img
                src={currentImage.url}
                alt="visual assessment prompt"
                className="w-full rounded-lg object-contain max-h-[350px] border border-gray-100 mx-auto"
              />
            )}

            <p className="text-center text-gray-500 mt-3 text-sm">
              <strong>{t("imageAssessment.promptLabel")}:</strong>{" "}
              {t("imageAssessment.promptText")} | {t("imageAssessment.image")}{" "}
              {currentIndex + 1} {t("imageAssessment.of")} {images.length}
            </p>
          </div>

          {/* RESPONSE SECTION */}
          <div className="mb-6 p-6 bg-white border border-gray-200 rounded-xl shadow-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 text-center">
              {t("imageAssessment.yourResponse")} 🎙️
            </h2>

            <div className="flex gap-6 justify-center mb-4">
              <button
                onClick={startRecording}
                disabled={status === "recording"}
                className="w-28 h-12 bg-green-600 text-white rounded-xl shadow-md disabled:bg-gray-300"
              >
                {status === "recording"
                  ? t("imageAssessment.recording")
                  : t("imageAssessment.start")}
              </button>

              <button
                onClick={stopRecording}
                disabled={status !== "recording"}
                className="w-28 h-12 bg-red-600 text-white rounded-xl shadow-md disabled:bg-gray-300"
              >
                {t("imageAssessment.stop")}
              </button>
            </div>

            {mediaBlobUrl && (
              <audio
                src={mediaBlobUrl}
                controls
                className="w-full my-4 rounded-lg border border-gray-300"
              />
            )}

            <button
              onClick={handleSubmitRecording}
              disabled={!mediaBlobUrl || assessmentResult}
              className="w-full px-4 py-3 bg-indigo-500 text-white rounded-lg shadow-md disabled:bg-indigo-300"
            >
              {t("imageAssessment.submit")}
            </button>

            {/* CHANGED: Render translation dynamically based on key stored in state */}
            {submissionStatus && (
              <p className="text-sm text-gray-600 mt-3 text-center">
                {t(submissionStatus.key)} {submissionStatus.suffix || ""}
              </p>
            )}
          </div>

          {/* FEEDBACK SECTION */}
          {assessmentResult && (
            <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-lg mb-4">
              <h2 className="text-xl font-semibold mb-3 text-gray-800">
                {t("imageAssessment.feedbackTitle")} 📋
              </h2>

              <div className="bg-white p-4 rounded-lg border border-indigo-200">
                <ReactMarkdown>{imageFeedback}</ReactMarkdown>
              </div>

              <button
                onClick={handleNext}
                className="w-full mt-4 bg-indigo-700 text-white px-4 py-3 rounded-lg shadow-md hover:bg-indigo-800 transition"
              >
                {currentIndex + 1 === images.length
                  ? t("imageAssessment.finish") || "Finish Assessment"
                  : t("imageAssessment.nextImage")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageAssessment;